import { computed } from "vue";
import { useStore } from "vuex";
import { useRouter } from "vue-router";

import dayjs from "@/utils/dayjs";
import { usePaymentQuick } from "@/lib/outbound/usePayment";
import { useClient } from "@/lib/_API/useClient";
import { useCharge } from "@/lib/_API/useCharge";
import cloneDeep from "lodash/cloneDeep";

/**
 *
 * Outbound - Quick(퀵)에서 사용되는 공통 state, methods 입니다.
 *
 * 주문접수/결제 단계 까지 같이 사용 됩니다.
 *
 */
export default function useQuick() {
  return {
    ...useQuickState(),
    ...useQuickUpdate(),
    ...useQuickAction(),
    ...useQuickGetters(),
  };
}
/**
 * 출/도착지 데이터 모델
 */
export const MODEL_PIN = (): outbound.pin => {
  return cloneDeep({
    name: "",
    phoneNumber: {
      value: "",
      isValidate: null,
    },
    jibunAddress: "",
    roadAddress: "",
    detailAddress: "",
    dongName: "",
    postalCode: "",
    latitude: 0,
    longitude: 0,
    pickupTime: "",
  });
};
/**
 * 물품정보 데이터 모델
 */
export const MODEL_ITEM = (): outbound.quick.item => {
  return cloneDeep({
    category: "",
    count: 1,
    weight: 0,
    price: 0,
    etc: "",
    weightOver: false,
    weightOverValue: "",
  });
};
/**
 * 화물 - 차량종류,물품정보 데이터 모델
 */
export const MODEL_FREIGHT_ITEM = (): outbound.quick.freightItem => {
  return cloneDeep({
    freightCategory: "",
    freightType: "damasNormal",
    itemEtc: "",
    // itemPrice: 0,
    loadPosition: "",
    loadMethod: "loadingYourself",
    unloadPosition: "",
    unloadMethod: "unloadingYourself",
  });
};
/**
 * 요금정보 데이터 모델
 */
export const MODEL_FEE = (): outbound.quick.fee => {
  return cloneDeep({
    totalCharge: 0, // VAT 포함가격
    chargeUuid: "",
  });
};

/**
 * 주문정보 데이터 모델
 */

export const MODEL_ORDERINFOMATION = (): outbound.quick.orderInfomation => {
  return cloneDeep({
    checked: true, // default true (주문선택)
    policyItem: false,
    clientComment: "",
    pickupFromCenter: true, // 지점출발여부 (true === '지점출발')
    expectedTime: "",
    vehicle: "bike",
    isRoundTrip: false,
    isReserved: false,
    pickupTime: "",
    customFields: [],
    isExpressQuick: false, //급송여부
  });
};
/**
 * Package 데이터 모델
 */
export const MODEL_PACKAGE = (): outbound.quick.package => {
  return cloneDeep({
    origin: MODEL_PIN(),
    destination: MODEL_PIN(),
    item: MODEL_ITEM(),
    fee: MODEL_FEE(),
    orderInfomation: MODEL_ORDERINFOMATION(),
  });
};

/**
 *
 *
 * 퀵 상태 값을 읽습니다.
 *
 *
 */
export function useQuickState() {
  const store = useStore();
  /**
   * 작성중인 출발지 정보
   */
  const quickOrigin = computed<Readonly<outbound.pin>>(
    () => store.state.outboundQuick.packageOrigin
  );
  /**
   * 작성중인 도착지 정보
   */
  const quickDestination = computed<Readonly<outbound.pin>>(
    () => store.state.outboundQuick.packageDestination
  );
  /**
   * 작성중인 물품 정보
   */
  const quickItem = computed<Readonly<outbound.quick.item>>(
    () => store.state.outboundQuick.packageItem
  );
  /**
   * 작성중인 화물 물품정보
   */
  const quickFreightItem = computed<Readonly<outbound.quick.freightItem>>(
    () => store.state.outboundQuick.packageFreightItem
  );
  /**
   * 작성중인 요금 정보
   */
  const quickFee = computed<Readonly<outbound.quick.fee>>(
    () => store.state.outboundQuick.packageFee
  );
  /**
   * 요금이 계산중인지 확인 (계산중 true, 계산완료 false)
   */
  const isCalculating = computed<boolean>(
    () => quickFee.value.totalCharge === null
  );
  /**
   * 작성중인 주문정보
   */
  const quickOrderInfomation = computed<
    Readonly<outbound.quick.orderInfomation>
  >(() => store.state.outboundQuick.packageOrderInfomation);

  /**
   * 작성 완료된 주문 정보
   */
  const quickCompleteList = computed<Readonly<outbound.quick.package[]>>(
    () => store.state.outboundQuick.completeList
  );

  //접수가능 시간 안내 팝업 표시 여부
  const receptionTimePopup = computed<boolean>(
    () => store.state.outboundQuick.receptionTimePopup
  );

  return {
    quickOrigin,
    quickDestination,
    quickItem,
    quickFreightItem,
    quickFee,
    quickOrderInfomation,
    quickCompleteList,
    receptionTimePopup,
    isCalculating,
  };
}

/**
 *
 *
 * 퀵 상태 값을 단순 업데이트 합니다.
 *
 *
 */
export function useQuickUpdate() {
  const store = useStore();

  /**
   * 출발지 정보 업데이트
   */
  const updateOrigin = <T extends outbound.pin, K extends keyof T>(
    key: K,
    value: T[K]
  ) => {
    store.dispatch("outboundQuick/updatePackageOrigin", {
      key: key,
      value: value,
    });
  };
  /**
   * 도착지 정보 업데이트
   */
  const updateDestination = <T extends outbound.pin, K extends keyof T>(
    key: K,
    value: T[K]
  ) => {
    store.dispatch("outboundQuick/updatePackageDestination", {
      key: key,
      value: value,
    });
  };
  /**
   * 물품정보 업데이트
   */
  const updateItem = <T extends outbound.quick.item, K extends keyof T>(
    key: K,
    value: T[K]
  ) => {
    store.dispatch("outboundQuick/updatePackageItem", {
      key: key,
      value: value,
    });
  };
  /**
   * 화물 - 물품정보 업데이트
   */
  const updateFreightItem = <T extends outbound.quick.freightItem, K extends keyof T>(
    key: K,
    value: T[K]
  ) => {
    store.dispatch("outboundQuick/updatePackageFreightItem", {
      key: key,
      value: value,
    });
  };
  /**
   * 요금정보 업데이트
   */
  const updateFee = <T extends outbound.quick.fee, K extends keyof T>(
    key: K,
    value: T[K]
  ) => {
    store.dispatch("outboundQuick/updatePackageFee", {
      key: key,
      value: value,
    });
  };
  /**
   * 주문정보 업데이트
   */
  const updateOrderInfomation = <
    T extends outbound.quick.orderInfomation,
    K extends keyof T
  >(
    key: K,
    value: T[K]
  ) => {
    store.dispatch("outboundQuick/updatePackageOrderInfomation", {
      key: key,
      value: value,
    });
  };
  /**
   * 주문완료목록 업데이트
   *
   * 배열초기화는 []를 넘겨줍니다.
   */
  const updateCompleteList = (value: outbound.quick.package[]) => {
    store.dispatch("outboundQuick/updateCompleteList", cloneDeep([...value]));
  };

  /**
   * 출발지 정보 초기화
   */
  const resetOrigin = <T extends outbound.pin, K extends keyof T>() => {
    const key = Object.keys(MODEL_PIN()) as Array<K>;
    const value = Object.values(MODEL_PIN()) as Array<T[K]>;

    key.forEach((el: K, index: number) => updateOrigin(el, value[index]));
  };
  /**
   * 도착지 정보 초기화
   */
  const resetDestination = <T extends outbound.pin, K extends keyof T>() => {
    const key = Object.keys(MODEL_PIN()) as Array<K>;
    const value = Object.values(MODEL_PIN()) as Array<T[K]>;

    key.forEach((el: K, index: number) => updateDestination(el, value[index]));
  };
  /**
   * 일반 퀵 - 물품 정보 초기화
   */
  const resetItem = <T extends outbound.quick.item, K extends keyof T>() => {
    const key = Object.keys(MODEL_ITEM()) as Array<K>;
    const value = Object.values(MODEL_ITEM()) as Array<T[K]>;

    key.forEach((el: K, index: number) => updateItem(el, value[index]));
  };
  /**
   * 화물 퀵 - 차량종류,물품,이용목적 초기화
   */
  const resetFreightItem = <T extends outbound.quick.freightItem, K extends keyof T>() => {
    const key = Object.keys(MODEL_FREIGHT_ITEM()) as Array<K>;
    const value = Object.values(MODEL_FREIGHT_ITEM()) as Array<T[K]>;

    key.forEach((el: K, index: number) => updateFreightItem(el, value[index]));
    updateFreightItem("itemPrice",undefined); //물품가액 별도 제거 
  };
  /**
   * 요금 정보 초기화
   */
  const resetFee = <T extends outbound.quick.fee, K extends keyof T>() => {
    const key = Object.keys(MODEL_FEE()) as Array<K>;
    const value = Object.values(MODEL_FEE()) as Array<T[K]>;

    key.forEach((el: K, index: number) => updateFee(el, value[index]));
  };
  /**
   * 주문 정보 초기화
   */
  const resetOrderInfomation = <
    T extends outbound.quick.orderInfomation,
    K extends keyof T
  >(
    type: string
  ) => {
    const key = Object.keys(MODEL_ORDERINFOMATION()) as Array<K>;
    const value = Object.values(MODEL_ORDERINFOMATION()) as Array<T[K]>;

    key.forEach((el: K, index: number) => {
      if (type === "all") {
        if (el === "customFields") {
          const customFields =
            store.state.outboundQuick.packageOrderInfomation.customFields.map(
              (el: any) => {
                return { ...el, value: "" };
              }
            );
          //커스텀필드 초기값으로 업데이트
          updateOrderInfomation("customFields", customFields);
        } else {
          updateOrderInfomation(el, value[index]);
        }
      } else {
        if (el === "customFields") {
          const customFields =
            store.state.outboundQuick.packageOrderInfomation.customFields.map(
              (el: any) => {
                return { ...el, value: "" };
              }
            );
          //커스텀필드 초기값으로 업데이트
          updateOrderInfomation("customFields", customFields);
        } else if (el !== "policyItem" && el !== "pickupFromCenter") {
          updateOrderInfomation(el, value[index]);
        }
      }
    });
  };
  /**
   * 현재 작성중인 주문 초기화 
   * 퀵 - 오토바이 탭
   * @isResetOrigin :boolean  출발지 초기화 여부
   * @type :string ("all"이면 정책동의까지 전부, "다른타입"이면 제외하고 리셋 )
   */
  const resetBikePackage = (isResetOrigin: boolean = false, type: string) => {
    isResetOrigin ? resetOrigin() : "";
    resetDestination();
    resetItem();
    resetOrderInfomation(type);
    resetFee();
    resetValidateInfo();
  };

  /**
   * 현재 작성중인 주문 초기화 
   *  퀵 - 화물탭 
   * @isResetOrigin :boolean  출발지 초기화 여부
   * @type :string ("all"이면 정책동의까지 전부, "다른타입"이면 제외하고 리셋 )
   */
  const resetFreightPackage = (isResetOrigin: boolean = false, type: string) => {
    isResetOrigin ? resetOrigin() : "";
    resetDestination();
    resetFreightItem();
    resetOrderInfomation(type);
    resetFee();
    resetValidateInfo();
  }

  const resetValidateInfo = () => {
    store.dispatch("outboundQuick/resetValidateInfo");
  };

  //필수 입력 항목 체크 팝업 표시 여부 업데이트
  const updateValidatePopup = (value: boolean) => {
    return (store.state.outboundQuick.validatePopup = value);
  };

  //지점 운영시간 안내 팝업 표시 여부 업데이트
  const updateReceptionTimePopup = (value: boolean) => {
    return (store.state.outboundQuick.receptionTimePopup = value);
  };

  return {
    updateOrigin,
    updateDestination,
    updateItem,
    updateFreightItem,
    updateFee,
    updateOrderInfomation,
    updateCompleteList,
    resetOrigin,
    resetDestination,
    resetItem,
    resetFreightItem,
    resetFee,
    resetOrderInfomation,
    resetBikePackage,
    resetFreightPackage,
    resetValidateInfo,
    updateValidatePopup,
    updateReceptionTimePopup,
  };
}

/**
 *
 * 퀵접수에 사용 되는 공통 action을 정의합니다.
 *
 */
export function useQuickAction() {
  const router = useRouter();
  const { getClientInfo } = useClient();
  const { chargeQuick, chargeQuickFreight } = useCharge();
  const { updateQuickPaymentList, updateCanUsePayment } = usePaymentQuick();
  const {
    quickItem,
    quickFreightItem,
    quickOrigin,
    quickDestination,
    quickCompleteList,
    quickOrderInfomation,
  } = useQuickState();
  const {
    updateFee,
    updateOrigin,
    updateDestination,
    updateOrderInfomation,
    resetFee,
    updateValidatePopup,
    updateReceptionTimePopup,
  } = useQuickUpdate();
  const { getCurrentOrder } = useQuickGetters();

  const originPickupTime = (orderInfo: outbound.quick.orderInfomation) => {
    if (orderInfo.isReserved){
      const availableTime = dayjs().add(60, "minute");
      const defaultTime = dayjs().add(75, "minute").format("YYYY-MM-DDTHH:mm:ss+09:00");
      //예약시간설정 후 시간경과시 픽업허용시간(현재+60분)이면 그대로사용, 아니면 디폴트 75분 부여 
      return availableTime.isBefore(orderInfo.pickupTime) ? orderInfo.pickupTime : defaultTime;

    } else {
      return dayjs().format("YYYY-MM-DDTHH:mm:ss+09:00");
    }
  };



  /**
   * 출발지, 도착지가 같은지 확인
   * @param origin 출발지
   * @param destination 도착지
   * @returns 출발지, 도착지가 같은지 여부 (T/F)
   */
  const isSamePinAddress = (
    origin: outbound.pin,
    destination: outbound.pin
  ) => {
    // 출발지 = 도착지 -> 지번주소가 같은 경우, 도로명 주소가 같은 경우, 위+경도가 같은 경우
    if (origin.jibunAddress === destination.jibunAddress) return true;
    if (origin.roadAddress === destination.roadAddress) return true;

    if (
      origin.latitude === destination.latitude &&
      origin.longitude === destination.longitude
    )
      return true;
    return false;
  };

  /**
   * 실제 요금 계산 api요청
   * return 성공시 최종요금 리턴 (요금변경 있는지 확인 위함.)
   */
  const chargeFee = async () => {
    const weight = quickItem.value.weight;
    const origin = quickOrigin.value;
    const destination = quickDestination.value;
    const orderInfo = quickOrderInfomation.value;
    const category = quickItem.value.category;

    if (!weight || !origin.postalCode || !destination.postalCode) {
      return resetFee();
    }

    // 출발지 도착지가 같은 경우, originPickupTime이 ""인경우 계산 x
    if (
      isSamePinAddress(origin, destination) ||
      (!originPickupTime(orderInfo) && orderInfo.isReserved)
    ) {
      resetFee();
      return;
    }

    const payload: api.charge.quick.payload = {
      pickupTime: originPickupTime(orderInfo) ,
      origin: {
        latitude: origin.latitude,
        longitude: origin.longitude,
      },
      destination: {
        latitude: destination.latitude,
        longitude: destination.longitude,
      },
      item: {
        weight: weight,
        document: category==="서류" ? quickItem.value.count : 0,
        smallBox: category==="소박스" ? quickItem.value.count : 0,
        bigBox: category==="대박스" ? quickItem.value.count : 0,
        etc: category==="기타" ? quickItem.value.etc : "",
      },
      vehicle: {
        category: orderInfo.vehicle,
      },
      isRoundTrip: orderInfo.isRoundTrip,
      isReserved: orderInfo.isReserved,
      isExpress: orderInfo.isExpressQuick
    };

    /**
     * 품목선택안했거나, 품목=기타인데 입력값없이 요금조회 하면,
     * 두가지 항목이 요금에 영향은 미치지않지만 api 필수값이므로 디펜스 디폴트값 '_' 설정.
     * (접수 전 필수검사때에 걸리기때문에 통과 후 요금조회 api 최종 한번더 호출.)
     */
    if(quickItem.value.category==="" || (category==="기타"&&quickItem.value.etc==="")){
      payload.item.etc = "_";
    }

    try {
      updateFee("totalCharge", null);
      const { chargeInfo } = await chargeQuick(payload);
      
      // 출발지 픽업시간 설정
      updateOrigin(
        "pickupTime",
        dayjs(originPickupTime(orderInfo)).utc().format()
      );
      // 도착지 픽업시간 설정
      updateDestination(
        "pickupTime",
        dayjs(originPickupTime(orderInfo))
          .add(chargeInfo.estimatedMinutes, "minutes")
          .utc()
          .format()
      );

      // 예상시간
      updateOrderInfomation("expectedTime", String(chargeInfo.estimatedMinutes));
      // uuid
      updateFee("chargeUuid", chargeInfo.chargeUuid);
      // 요금
      updateFee("totalCharge", chargeInfo.totalCharge);

      return chargeInfo.totalCharge; //최종요금값 리턴
    } catch (e) {
      // 실패 시 요금 0
      resetFee();
    }
  };

  //퀵-화물요금조회
  const chargeFreightFee = async () => {
    const origin = quickOrigin.value;
    const destination = quickDestination.value;
    const orderInfo = quickOrderInfomation.value;
    const { freightCategory,freightType,itemEtc,
      loadPosition,loadMethod,unloadPosition,unloadMethod } = quickFreightItem.value;

    if (!origin.postalCode || !destination.postalCode || !freightCategory) {
      return resetFee();
    }

    // 출발지 도착지가 같은 경우, originPickupTime이 ""인경우 계산 x
    if (
      isSamePinAddress(origin, destination) ||
      (!originPickupTime(orderInfo) && orderInfo.isReserved)
    ) {
      resetFee();
      return;
    }

    const payload: api.charge.freightPayload = {
      origin: {
        latitude: origin.latitude,
        longitude: origin.longitude,
        loadPosition: loadPosition,
        loadMethod: loadMethod
      },
      destination: {
        latitude: destination.latitude,
        longitude: destination.longitude,
        unloadPosition: unloadPosition,
        unloadMethod: unloadMethod
      },
      item: {
        etc: itemEtc
      },
      vehicle: {
        category: freightCategory,
        option: freightType
      },
      isExpress: orderInfo.isExpressQuick,
      isRoundTrip: orderInfo.isRoundTrip,
      isReserved: orderInfo.isReserved,
      pickupTime: originPickupTime(orderInfo) ,
    };

    /** api required - default setting  */
    if(itemEtc ==="") payload.item.etc = "_";
    if(loadPosition ==="")payload.origin.loadPosition = "loadingParkingSpace";
    if(unloadPosition ==="")payload.destination.unloadPosition = "unloadingParkingSpace";
    if(["labo","1t"].includes(freightCategory) && freightType ==="damasNormal"){
      payload.vehicle.option = "cargo"; //요금조회 로딩중에 watch되면 싱크 맞지않을경우 defense
    } 

    try {
      updateFee("totalCharge", null);
      const { chargeInfo } = await chargeQuickFreight(payload);
      
      // 출발지 픽업시간 설정
      updateOrigin(
        "pickupTime",
        dayjs(originPickupTime(orderInfo)).utc().format()
      );
      // 도착지 픽업시간 설정
      updateDestination(
        "pickupTime",
        dayjs(originPickupTime(orderInfo))
          .add(chargeInfo.estimatedMinutes, "minutes")
          .utc()
          .format()
      );

      // 예상시간
      updateOrderInfomation("expectedTime", String(chargeInfo.estimatedMinutes));
      // uuid
      updateFee("chargeUuid", chargeInfo.chargeUuid);
      // 요금
      updateFee("totalCharge", chargeInfo.totalCharge);

      return chargeInfo.totalCharge; //최종요금값 리턴
    } catch (e) {
      // 실패 시 요금 0
      resetFee();
    }
  };

  /**
   * 출발지 정보 로그인한 사용자 정보로 업데이트
   * status : 지점출발 여부 (true : 지점출발, false : 직접전달)
   */
  const actionOriginUpdateUser = async (status: boolean) => {
    try {
      const { client, center } = await getClientInfo();

      updateOrigin("name", client["name"]);
      updateOrigin("phoneNumber", {
        value: client["phoneNumber"],
        isValidate: true,
      });

      if (status) {
        updateOrigin("dongName", center["dongName"]);
        updateOrigin("jibunAddress", center["jibunAddress"]);
        updateOrigin("roadAddress", center["roadAddress"]);
        updateOrigin("detailAddress", center["detailAddress"]);
        updateOrigin("latitude", center["latitude"]);
        updateOrigin("longitude", center["longitude"]);
        updateOrigin("postalCode", center["postalCode"]);
        updateOrigin("pickupTime", dayjs.utc().format());
      }
    } catch {}
  };
  
  /**
   * 주문접수
   *
   */
  const actionSubmitOutbound = () => {

      // 결제대기 목록으로 이동 시킵니다.
      updateQuickPaymentList([
        ...quickCompleteList.value,
        getCurrentOrder.value,
      ]);
      // router.push("/outbound/quick/payment");
      // 결제하기 모달 open
      updateCanUsePayment(true);
  };

  /**
   *  결제대기 목록을 주문접수합니다.
   *
   *  결제대기 목록들의 pickupTime을 설정합니다.
   */
  const actionSubmitPayment = () => {};

  //현재 주문 필수 입력항목 팝업 표시 여부를 설정합니다

  const actionValidatePopup = (value: boolean) => {
    updateValidatePopup(value);
  };

  const actionReceptionTimePopup = (value: boolean) => {
    updateReceptionTimePopup(value);
  };

  return {
    actionSubmitOutbound,
    actionSubmitPayment,
    actionOriginUpdateUser,
    actionValidatePopup,
    actionReceptionTimePopup,
    chargeFee,
    chargeFreightFee
  };
}

/**
 *  getters
 */
export function useQuickGetters() {
  const {
    quickCompleteList,
    quickFee,
    quickOrderInfomation,
    quickOrigin,
    quickDestination,
    quickItem,
    quickFreightItem
  } = useQuickState();

  /**
   * 현재 작성중인 주문
   */
  const getCurrentOrder = computed<outbound.quick.package>(() => {
    return {
      origin: quickOrigin.value,
      destination: quickDestination.value,
      item: quickItem.value,
      fee: quickFee.value,
      orderInfomation: quickOrderInfomation.value,
      freightItem: quickFreightItem.value
    };
  });

  /**
   * 모든 주문 총 금액
   */
  const getTotalFee = computed<Readonly<number>>(() => {
    // 완료주문의 총 금액
    const completeFee = quickCompleteList.value.reduce((stack, el, index) => {
      const elFee = el.fee.totalCharge!;
      return stack + elFee;
    }, 0);
    // 현재 주문의 금액
    const currentFee = quickFee.value.totalCharge!;
    return completeFee + currentFee;
  });

  /**
   *  퀵- 오토바이 접수에 필요한 주문정보가 모두 있는지 확인
   *  false = 접수불가, true = 접수가능
   */
  const getCompleteCondition = computed<Readonly<boolean>>(() => {
    const _conditionPin = (pin: outbound.pin) => {
      if (!pin.phoneNumber.isValidate) return false; // 핸드폰번호 검증 안된경우
      const checkKey = ["name", "postalCode", "detailAddress", "pickupTime"];
      let checkEmptyValue = true;

      checkKey.forEach((element) => {
        if (!pin[element]) checkEmptyValue = false;
      });

      return checkEmptyValue;
    };

    const conditionOrigin = _conditionPin(quickOrigin.value);
    const conditionDestination = _conditionPin(quickDestination.value);
    const conditionItemPrice = !quickItem.value.price ? false : true;

    const conditionItemWeight = () => {
      if ([3, 5, 10, 20].includes(quickItem.value.weight)) return true;
      else if (quickItem.value.weight > 20) return true;
      else return false;
    };

    const conditionItemCategory = () => {
      if (!quickItem.value.category) return false;
      if (quickItem.value.category === "기타") {
        return quickItem.value.etc ? true : false;
      } else {
        return quickItem.value.count ? true : false;
      }
    };
    const conditionFee = !quickFee.value.chargeUuid ? false : true;

    const conditionCustomFields = () => {
      let isPass = false;
      const customArray = quickOrderInfomation.value.customFields;
      if (customArray.length)
        isPass = customArray.every((el: any) => {
          return el.value;
        });
      else isPass = true;

      return isPass;
    };

    const conditionReserveTime = () => {
      if (!quickOrderInfomation.value.isReserved) {
        return true;
      } else {
        const reserveTime = quickOrderInfomation.value.pickupTime;
        const now = dayjs().format("YYYY-MM-DDTHH:mm:00+09:00");
        const after60min = dayjs(now).add(60, "minute"); //default 75minutes up
        const validSuccess = after60min.isBefore(reserveTime, "minute");
        return validSuccess;
      }
    }

    return !conditionOrigin ||
      !conditionDestination ||
      !conditionItemPrice ||
      !conditionItemWeight() ||
      !conditionItemCategory() ||
      !conditionFee ||
      !conditionCustomFields() ||
      !conditionReserveTime()
      ? false
      : true;
  });

  /**
   *  퀵- 화물 접수에 필요한 주문정보가 모두 있는지 확인
   *  false = 접수불가, true = 접수가능
   */
  const getCompleteFreightCondition = computed<Readonly<boolean>>(() => {
    const _conditionPin = (pin: outbound.pin) => {
      if (!pin.phoneNumber.isValidate) return false; // 핸드폰번호 검증 안된경우
      const checkKey = ["name", "postalCode", "detailAddress", "pickupTime"];
      let checkEmptyValue = true;
      checkKey.forEach((element) => {
        if (!pin[element]) checkEmptyValue = false;
      });
      return checkEmptyValue;
    };

    const conditionOrigin = _conditionPin(quickOrigin.value);
    const conditionDestination = _conditionPin(quickDestination.value);

    const { freightCategory,itemEtc,itemPrice,loadPosition,unloadPosition } = quickFreightItem.value;
    const conditionFreightCategory = !freightCategory ? false : true;
    const conditionItemEtc = !itemEtc ? false : true;
    const conditionItemPrice = !itemPrice ? false : true;
    const conditionLoadPosition = !loadPosition ? false : true;
    const conditionUnloadPosition = !unloadPosition ? false : true;

    const conditionFee = !quickFee.value.chargeUuid ? false : true;

    const conditionCustomFields = () :boolean => {
      let isPass = false;
      const customArray = quickOrderInfomation.value.customFields;
      if (customArray.length)
        isPass = customArray.every((el: any) => {
          return el.value;
        });
      else isPass = true;
      return isPass;
    };

    const conditionReserveTime = () :boolean => {
      if (!quickOrderInfomation.value.isReserved) {
        return true;
      } else {
        const reserveTime = quickOrderInfomation.value.pickupTime;
        const now = dayjs().format("YYYY-MM-DDTHH:mm:00+09:00");
        const after60min = dayjs(now).add(60, "minute"); //default 75minutes up
        const validSuccess = after60min.isBefore(reserveTime, "minute");
        return validSuccess;
      }
    }
    const targetArray = [ conditionOrigin, conditionDestination, conditionFreightCategory
      , conditionItemEtc, conditionItemPrice, conditionLoadPosition, conditionUnloadPosition
      , conditionFee, conditionCustomFields(), conditionReserveTime()];

    return targetArray.every((target : boolean) => target === true) ? true : false;
  });

  /**
   * 퀵- 오토바이 주문접수 가능 한 상태인지 확인
   * 접수가능(true), 접수 불가능(false)
   */
  const getSubmitCondition = computed<Readonly<boolean>>(() => {
    const conditionPolicy = quickOrderInfomation.value.policyItem
      ? true
      : false; // 물품약관 동의

    return !conditionPolicy || !getCompleteCondition.value ? false : true;
  });

  /**
   * 퀵- 화물 주문접수 가능 한 상태인지 확인
   * 접수가능(true), 접수 불가능(false)
   */
  const getSubmitFreightCondition = computed<Readonly<boolean>>(() => {
    const conditionPolicy = quickOrderInfomation.value.policyItem
      ? true
      : false; // 물품약관 동의

    return !conditionPolicy || !getCompleteFreightCondition.value ? false : true;
  });

  /**
   * 완료주문 + 현재 작성 중인 주문
   */
  const getAllOrders = computed<Readonly<outbound.quick.package[]>>(() => {
    return [...quickCompleteList.value, getCurrentOrder.value];
  });

  return {
    getTotalFee,
    getSubmitCondition,
    getCompleteCondition,
    getSubmitFreightCondition,
    getCompleteFreightCondition,
    getAllOrders,
    getCurrentOrder,
  };
}
